import { AnyAction, createSlice, PayloadAction, createDraftSafeSelector } from '@reduxjs/toolkit';
import { ActionsObservable, combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { api } from '~constants/api';
import { extractPaginationValues } from '~features/page/pageUtils';

import { actions as notificationsActions } from '../notifications/notificationsSlice';
import { actions as pageActions } from '../page/pageSlice';
import { actions as sharedActions } from '../sharedSlice';

import { BusinessUnit, Fault, FaultBUListSections, FaultsCountList, IPLink, PaginationResponse, ResponseError, Subscriber } from '~models';
import { http } from '~services';
import { getResponseError, intToHex } from '~utils';

export interface FaultsState {
  data: {
    all: Fault[];
    section: { [key in FaultBUListSections]: FaultsCountList };
  };
  loading: {
    all: boolean;
    restore: boolean;
    section: { [key in FaultBUListSections]: boolean };
  };
  error?: ResponseError | null;
}

export const initialState: FaultsState = {
  data: {
    all: [],
    section: {
      units: {},
      'ip-links': {}
    },
  },
  loading: {
    all: false,
    restore: false,
    section: {
      units: false,
      'ip-links': false,
    },
  },
};

export const { name, actions, reducer } = createSlice({
  name: 'faults',
  initialState,
  reducers: {
    // Fetch faults
    fetchFaultsInit(
      state,
      { payload }: PayloadAction<{
        apiUrl: string;
        message?: string;
        notify?: boolean;
      }>
    ) {
      state.loading.all = true;
    },
    fetchFaultsSuccess(
      state,
      {
        payload: { content },
      }: PayloadAction<PaginationResponse<Fault> & { section?: string }>
    ) {
      state.loading.all = false;
      state.data.all = content;
    },
    fetchFaultsFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.all = false;
      state.error = payload;
    },

    // Fetch faults event counts
    fetchFaultsCountInit(
      state,
      { payload }: PayloadAction<{
        id: BusinessUnit['id'],
        section: FaultBUListSections;
      }>
    ) {
      state.loading[payload.section] = true;
    },
    fetchFaultsCountSuccess(
      state,
      {
        payload: { values, section },
      }: PayloadAction<{ values: FaultsCountList, section: FaultBUListSections }>
    ) {
      state.data[section] = values;
      state.loading[section] = false;
    },
    fetchFaultsCountFailed(
      state, { payload: { section, error } }: PayloadAction<{ section: FaultBUListSections; error: ResponseError }>
    ) {
      state.loading[section] = false;
      state.error = error;
    },

    restoreUnitFaultInit(
      state,
      {
        payload,
      }: PayloadAction<{
        buId: IPLink['businessUnitId'] | Subscriber['businessUnitId']
        cid: Fault['cid'];
        eventCode: Fault['eventCode'];
        id: IPLink['id'] | Subscriber['id'];
        zoneCode: Fault['zoneCode'];
        unitType: string;
      }>
    ) {
      state.loading.restore = true;
    },

    restoreUnitFaultSuccess(state) {
      state.loading.restore = false;
    },

    restoreUnitFaultFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.restore = false;
      state.error = payload;
    },

    // Reset faults
    resetFaults(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: {
    [sharedActions.reset.toString()]: state => Object.assign(state, initialState),
  },
});

const getFaultsState = (state: AES.RootState) => state.faults;
export const selectors = {
  getFaultsState,

  getFaults: createDraftSafeSelector(getFaultsState, state => state.data.all),
  getFaultsBySection: (section: string) => createDraftSafeSelector(getFaultsState, state => state.data[section]),
  getFaultsLoaderBySection: (section: string) =>
    createDraftSafeSelector(getFaultsState, state => state.loading[section]),
  isFaultsLoading: createDraftSafeSelector(getFaultsState, state => state.loading.all),
  getFaultsError: createDraftSafeSelector(getFaultsState, state => state.error),
};

export const fetchFaultsEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchFaultsInit.match),
    mergeMap(({ payload: { apiUrl, notify, message } }) =>
      http.getJSON<PaginationResponse<Fault>>(apiUrl).pipe(
        mergeMap(values => {
          const epicActions: AnyAction[] = [
            actions.fetchFaultsSuccess(values),
            pageActions.setPagePagination(extractPaginationValues(values)),
          ];

          if (notify && message) {
            epicActions.push(
              notificationsActions.enqueue({
                message,
                options: {
                  variant: 'success',
                },
              })
            );
          }

          return of(...epicActions);
        }),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchFaultsFailed(error));
        })
      )
    )
  );

export const fetchFaultsCountEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchFaultsCountInit.match),
    mergeMap(({ payload: { id, section } }) =>
      http.getJSON<FaultsCountList>(api.faults.faultsCount(id, section)).pipe(
        mergeMap(values => (
          of(
            actions.fetchFaultsCountSuccess({ values, section }),
          )
        )),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.fetchFaultsCountFailed({ error, section }));
        })
      )
    )
  );

const UnitFaultRestoreEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.restoreUnitFaultInit.match),
    mergeMap(({ payload: { buId, cid, eventCode, id, zoneCode, unitType } }) =>
      http.post(api.faults.restore(buId, id, unitType), { buId, cid, eventCode, id, zoneCode }).pipe(
        mergeMap(() =>
          of(
            actions.fetchFaultsInit({ apiUrl: api.faults.unitFaults(buId, id, unitType) }),
            actions.restoreUnitFaultSuccess(),
            notificationsActions.enqueue({
              message: `Fault Restored for Unit ID ${intToHex(id)}`,
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => {
          const error = getResponseError(err);

          return of(actions.restoreUnitFaultFailed(error));
        })
      )
    )
  );

export const epics = combineEpics(
  fetchFaultsEpic,
  fetchFaultsCountEpic,
  UnitFaultRestoreEpic
);
export const allEpics = {
  fetchFaultsEpic,
  fetchFaultsCountEpic,
  UnitFaultRestoreEpic
};

declare global {
  namespace AES {
    export interface Actions {
      faults: typeof actions;
    }
    export interface RootState {
      faults: FaultsState;
    }
    export interface Selectors {
      faults: typeof selectors;
    }
  }
}
